import React, { useMemo } from 'react';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useAppSelector } from 'app/hooks';
import { currencyFormatter, getMessage } from 'util/locale';
import { getCurrency } from 'user/userSelectors';
import { formatAvgYield, formatNumberK, formatNumSeedBags } from 'util/formatters/numberFormatter';
import { inferUnit } from 'util/unitConversion';
import { RecommendationOptions } from '../../../../types';
import { DeltaValueCell } from '../../../CustomCells';
import { DetailsTableRow } from '../types';
import { allowDecimalByCountry } from 'user/selectors';

export const useDetailsTable = ({ fvssRow, yourEntryRow }) => {
  const currency = useAppSelector(getCurrency);
  const isDecimalCountry = useAppSelector(allowDecimalByCountry);

  const columns: ColumnDef<DetailsTableRow>[] = useMemo(
    () => [
      {
        accessorKey: 'rowHeader',
        header: getMessage('details'),
      },
      {
        accessorKey: RecommendationOptions.FVSS_RECOMMENDATION,
        header: getMessage('text__recommended'),
        cell: ({ row, getValue }) => row.original.key === 'deltaValue' ? (
          <DeltaValueCell
            delta={Number(row.original[RecommendationOptions.FVSS_RECOMMENDATION])}
            currency={currency}
          />
        ) : (<span>{getValue()}</span>)
      },
      {
        accessorKey: RecommendationOptions.YOUR_ENTRY,
        header: getMessage('your-entry'),
        cell: ({ row, getValue }) => row.original.key === 'deltaValue' ? (
          <DeltaValueCell
            delta={Number(row.original[RecommendationOptions.YOUR_ENTRY])}
            currency={currency}
          />
        ) : (<span>{getValue()}</span>)
      }
    ], [currency]);

  const data = useMemo(() => [
    {
      key: 'avgFixedPop',
      rowHeader: getMessage('avg-planting-population'),
      [RecommendationOptions.FVSS_RECOMMENDATION]: formatNumberK(fvssRow.avgFixedPop),
      [RecommendationOptions.YOUR_ENTRY]: formatNumberK(yourEntryRow.avgFixedPop),
    },
    {
      key: 'bagsNeeded',
      rowHeader: getMessage('bags-needed'),
      [RecommendationOptions.FVSS_RECOMMENDATION]: formatNumSeedBags(fvssRow.bagsNeeded),
      [RecommendationOptions.YOUR_ENTRY]: formatNumSeedBags(yourEntryRow.bagsNeeded),
    },
    {
      key: 'estimatedYield',
      rowHeader: `${getMessage('estimated-yield')} (${inferUnit('area-density.yield').symbol.quantitySpecific})`,
      [RecommendationOptions.FVSS_RECOMMENDATION]: formatAvgYield(fvssRow.estimatedYield, isDecimalCountry),
      [RecommendationOptions.YOUR_ENTRY]: formatAvgYield(yourEntryRow.estimatedYield, isDecimalCountry),
    },
    {
      key: 'seedCostPerArea',
      rowHeader: getMessage('seed-cost-per-area', { areaUnit: inferUnit('area').label.universal }),
      [RecommendationOptions.FVSS_RECOMMENDATION]: currencyFormatter(
        fvssRow.seedCostPerArea,
        currency,
        { round: true }
      ),
      [RecommendationOptions.YOUR_ENTRY]: currencyFormatter(
        yourEntryRow.seedCostPerArea,
        currency,
        { round: true }
      ),
    },
    {
      key: 'projectedRevenuePerArea',
      rowHeader: getMessage('bulk-fvss-review-plan-expected-revenue'),
      [RecommendationOptions.FVSS_RECOMMENDATION]: currencyFormatter(
        fvssRow.projectedRevenuePerArea,
        currency,
        { round: true }
      ),
      [RecommendationOptions.YOUR_ENTRY]: currencyFormatter(
        yourEntryRow.projectedRevenuePerArea,
        currency,
        { round: true }
      ),
    },
    {
      key: 'deltaValue',
      rowHeader: getMessage('potential-gain-loss'),
      [RecommendationOptions.FVSS_RECOMMENDATION]:
            fvssRow.projectedRevenuePerArea - yourEntryRow.projectedRevenuePerArea,
      [RecommendationOptions.YOUR_ENTRY]:
          yourEntryRow.projectedRevenuePerArea - fvssRow.projectedRevenuePerArea,
    }
  ] as DetailsTableRow[], [
    currency,
    fvssRow.avgFixedPop,
    fvssRow.bagsNeeded,
    fvssRow.estimatedYield,
    fvssRow.projectedRevenuePerArea,
    fvssRow.seedCostPerArea,
    isDecimalCountry,
    yourEntryRow.avgFixedPop,
    yourEntryRow.bagsNeeded,
    yourEntryRow.estimatedYield,
    yourEntryRow.projectedRevenuePerArea,
    yourEntryRow.seedCostPerArea
  ]);

  return useReactTable<DetailsTableRow>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
};
